import type {
  OpencodeMessagePart,
  SessionMessageWithParts,
} from "schemas/generated/opencode";

import React, { useState } from "react";

import { MessageAction } from "@agent-ui/features/chat/components/chat-elements/ui/Message";
import { markdownToHtml } from "@agent-ui/features/chat/utils/markdownToHtml";
import { toast } from "@replo/design-system/components/toast/Toast";
import { CircleCheckIcon } from "@replo/design-system/icons/animated/CircleCheckIcon";
import { CopyIcon } from "@replo/design-system/icons/animated/CopyIcon";
import { useErrorToast } from "@v2/hooks/useErrorToast";
import { writeHtmlToClipboard } from "@v2/utils/clipboard";
import { filterNulls } from "replo-utils/lib/array";

function getTextFromParts(parts: OpencodeMessagePart[]): string {
  return filterNulls(
    parts.map((part) => {
      if (part.partData.type === "text" && part.partData.text) {
        return part.partData.text.trim();
      }
      return null;
    }),
  ).join("\n\n");
}

export function CopyAction({ message }: { message: SessionMessageWithParts }) {
  const errorToast = useErrorToast();
  const [isCopied, setIsCopied] = useState(false);

  const handleClick = async () => {
    try {
      const text = getTextFromParts(message.parts);
      if (!text) {
        return;
      }

      await writeHtmlToClipboard(markdownToHtml(text), errorToast);

      setIsCopied(true);
      toast({ title: "Message copied to clipboard" });

      setTimeout(() => {
        setIsCopied(false);
      }, 2000);
    } catch (error) {
      console.error("Failed to copy message:", error);
    }
  };

  return (
    <MessageAction
      icon
      tooltip="Copy message"
      onClick={() => void handleClick()}
    >
      {isCopied ? (
        <CircleCheckIcon className="text-emerald" />
      ) : (
        <CopyIcon className="text-muted" />
      )}
    </MessageAction>
  );
}
